// Firmas del trabajador (M45): lectura y escritura en la base y en Storage.
//  - Preparar: congela el contenido (snapshot + SHA-256) y bloquea el origen (trigger de M45).
//  - Firmar: sube el trazo PNG a worker-signatures (<empresa>/<registro>/) y registra la firma; la base pone el
//    nombre y la cédula de Trabajadores y la fecha y hora del servidor.
//  - Adjuntos: escaneo del formato firmado y certificado del médico. Fotos (JPG o PNG, hasta 5 MB) a worker-signatures;
//    PDF a sso-documents en <empresa>/firmas/<registro>/ (privado, políticas por empresa).
//  - Cerrar (firmado), volver a editar (sin firmas) y reabrir (RPC, solo admin, con motivo).
//  - Archivos con URL firmada de 120 s.

import { supabase } from '../../supabaseClient';
import { logAudit } from '../../auditLog';
import { revisarEvidencia, nombreSeguro, SEGUNDOS_URL_EVIDENCIA, type TipoEvidencia } from '../pe/pe-evidencias';
import {
  contenidoAptitud, contenidoAsistencia, contenidoEpp, hashContenido, hashFirma, mensajeErrorFirmas,
  type AsignacionOrigen, type AsistenteOrigen, type CapacitacionOrigen, type ExamenOrigen, type Firmante,
} from './firmas-logica';
import { BUCKET_DOCUMENTOS, BUCKET_FIRMAS, ETIQUETA_TIPO_REGISTRO } from './firmas-tipos';
import type { BucketRegistro, Contenido, FilaFirma, FilaRegistro, MetodoRegistro, TipoRegistro } from './firmas-tipos';

export const FOTO_MAX_MB = 5;
export const FOTO_MAX_BYTES = FOTO_MAX_MB * 1024 * 1024;
export const ACEPTA_ADJUNTO = '.jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf';

const falla = (e: { message?: string; code?: string } | null | undefined, accion: string): never => {
  throw new Error(mensajeErrorFirmas(e, accion));
};

/** ¿La base ya tiene M45? Sin las tablas, las pantallas no muestran nada de firmas. */
export async function firmasDisponibles(companyId: string): Promise<boolean> {
  if (!companyId) return false;
  const { error } = await supabase.from('signed_records').select('id', { head: true, count: 'exact' }).eq('company_id', companyId).limit(1);
  return !error;
}

// epp_assignment_id es null si la asignación se borró (el registro firmado se conserva).
export interface DatosFirmas { registros: FilaRegistro[]; firmas: FilaFirma[]; itemsEpp: { record_id: string; epp_assignment_id: string | null }[] }

export async function cargarFirmas(companyId: string, tipo?: TipoRegistro): Promise<DatosFirmas> {
  let q = supabase.from('signed_records').select('*').eq('company_id', companyId);
  if (tipo) q = q.eq('kind', tipo);
  const [{ data: r, error: e1 }, { data: f, error: e2 }, { data: i, error: e3 }] = await Promise.all([
    q.order('record_date', { ascending: false }),
    supabase.from('signed_record_signatures').select('*').eq('company_id', companyId).order('signed_at'),
    supabase.from('signed_record_epp_items').select('record_id, epp_assignment_id').eq('company_id', companyId),
  ]);
  if (e1 || e2 || e3) falla(e1 ?? e2 ?? e3, 'leer los registros firmados');
  const registros = ((r ?? []) as FilaRegistro[]).map(x => ({ ...x, reopen_history: x.reopen_history ?? [] }));
  const ids = new Set(registros.map(x => x.id));
  return {
    registros,
    firmas: ((f ?? []) as FilaFirma[]).filter(x => ids.has(x.record_id)),
    itemsEpp: (i ?? []) as DatosFirmas['itemsEpp'],
  };
}

// ── Contenido desde el origen ──

export async function contenidoDeCapacitacion(companyId: string, t: CapacitacionOrigen, empresa: string): Promise<Contenido> {
  const { data, error } = await supabase.from('sso_training_attendance')
    .select('worker_id, evaluation_result, evaluation_score, workers(full_name, cedula)')
    .eq('company_id', companyId).eq('training_id', t.id).eq('attended', true);
  if (error) falla(error, 'leer la lista de asistencia');
  const asistentes: AsistenteOrigen[] = ((data ?? []) as any[]).map(a => {
    const w = Array.isArray(a.workers) ? a.workers[0] : a.workers;
    return { worker_id: a.worker_id, nombre: w?.full_name ?? '', cedula: w?.cedula ?? null,
      evaluation_result: a.evaluation_result ?? null, evaluation_score: a.evaluation_score === null || a.evaluation_score === undefined ? null : Number(a.evaluation_score) };
  });
  return contenidoAsistencia(t, asistentes, empresa);
}

export const contenidoDeEntrega = (trabajador: { id: string; full_name: string; cedula: string | null }, fecha: string, items: AsignacionOrigen[], empresa: string) =>
  contenidoEpp(trabajador, fecha, items, empresa);

export const contenidoDeExamen = (e: ExamenOrigen, trabajador: { full_name: string; cedula: string | null }, empresa: string) =>
  contenidoAptitud(e, trabajador, empresa);

// ── Preparar, volver a editar ──

export interface OrigenRegistro { training_id?: string; medical_exam_id?: string; worker_id?: string; asignaciones?: string[]; procedure_version_id?: string }

/**
 * Congela el contenido y pasa el registro a "en firma". Si ya hay un registro abierto del mismo origen (después de
 * reabrir o de volver a editar), se reutiliza: su historial de reaperturas se conserva.
 */
export async function prepararRegistro(companyId: string, existente: FilaRegistro | null, contenido: Contenido, origen: OrigenRegistro,
  usuarioId: string | null): Promise<FilaRegistro> {
  const hash = await hashContenido(contenido);
  const fila = { status: 'en_firma', snapshot: contenido, content_hash: hash, record_date: contenido.fecha };
  let reg: FilaRegistro;
  if (existente) {
    if (existente.status !== 'abierto') throw new Error('El registro ya está en firma o firmado.');
    const { data, error } = await supabase.from('signed_records').update(fila).eq('id', existente.id).select('*');
    if (error) falla(error, 'preparar el registro para firma');
    if (!data?.length) throw new Error('No tienes permiso para preparar el registro.');
    reg = (data as FilaRegistro[])[0];
  } else {
    const { data, error } = await supabase.from('signed_records').insert({
      ...fila, company_id: companyId, kind: contenido.tipo, created_by: usuarioId,
      training_id: origen.training_id ?? null, medical_exam_id: origen.medical_exam_id ?? null, worker_id: origen.worker_id ?? null,
      // M49: solo la socialización lleva la versión del procedimiento (la columna no existe antes de M49).
      ...(origen.procedure_version_id ? { procedure_version_id: origen.procedure_version_id } : {}),
    }).select('*');
    if (error) falla(error, 'preparar el registro para firma');
    if (!data?.length) throw new Error('No tienes permiso para preparar el registro.');
    reg = (data as FilaRegistro[])[0];
  }
  if (contenido.tipo === 'epp_entrega') {
    const quiero = origen.asignaciones ?? contenido.items.map(i => i.asignacion_id);
    const { data: actuales, error: e1 } = await supabase.from('signed_record_epp_items').select('epp_assignment_id').eq('record_id', reg.id);
    if (e1) falla(e1, 'leer el EPP del registro');
    const tengo = new Set(((actuales ?? []) as { epp_assignment_id: string | null }[]).map(x => x.epp_assignment_id)
      .filter((x): x is string => !!x));
    const quitar = Array.from(tengo).filter(x => !quiero.includes(x));
    if (quitar.length) {
      const { error } = await supabase.from('signed_record_epp_items').delete().eq('record_id', reg.id).in('epp_assignment_id', quitar);
      if (error) falla(error, 'actualizar el EPP del registro');
    }
    const agregar = quiero.filter(x => !tengo.has(x)).map(x => ({ record_id: reg.id, company_id: companyId, epp_assignment_id: x }));
    if (agregar.length) {
      const { error } = await supabase.from('signed_record_epp_items').insert(agregar);
      if (error) falla(error, 'agregar el EPP al registro');
    }
  }
  registrarAuditoria(reg, 'registro_preparado', { hash });
  return { ...reg, reopen_history: reg.reopen_history ?? [] };
}

/** En firma y sin firmas: vuelve a abierto (el origen se puede editar otra vez). */
export async function volverAEditar(r: FilaRegistro): Promise<FilaRegistro> {
  const { data, error } = await supabase.from('signed_records').update({ status: 'abierto' }).eq('id', r.id).select('*');
  if (error) falla(error, 'volver a editar el registro');
  if (!data?.length) throw new Error('No tienes permiso para cambiar el registro.');
  return (data as FilaRegistro[])[0];
}

// ── Firmar ──

const rutaRegistro = (r: Pick<FilaRegistro, 'company_id' | 'id'>, archivo: string, marca = Date.now()) => `${r.company_id}/${r.id}/${marca}-${archivo}`;
const rutaDocumento = (r: Pick<FilaRegistro, 'company_id' | 'id'>, archivo: string, marca = Date.now()) => `${r.company_id}/firmas/${r.id}/${marca}-${archivo}`;

async function subir(bucket: BucketRegistro, ruta: string, cuerpo: Blob, tipo: string, accion: string): Promise<void> {
  const { error } = await supabase.storage.from(bucket).upload(ruta, cuerpo, { contentType: tipo, upsert: false });
  if (error) {
    const sinPermiso = /row-level security|unauthorized|403/i.test(error.message ?? '');
    throw new Error(sinPermiso ? `No tienes permiso para ${accion}.` : `No se pudo ${accion}: ${error.message}.`);
  }
}

/** Sube el trazo y registra la firma. El hash es el del contenido del registro más los datos del firmante. */
export async function firmar(r: FilaRegistro, firmante: Firmante, png: Blob): Promise<FilaFirma> {
  if (r.status !== 'en_firma' || !r.content_hash) throw new Error('Prepara el registro para firma antes de firmar.');
  if (png.size > FOTO_MAX_BYTES) throw new Error(`La firma supera ${FOTO_MAX_MB} MB.`);
  const hash = await hashFirma(r.content_hash, firmante);
  const ruta = rutaRegistro(r, `firma_${firmante.rol}${firmante.worker_id ? `_${firmante.worker_id.slice(0, 8)}` : ''}.png`);
  await subir(BUCKET_FIRMAS, ruta, png, 'image/png', 'subir la firma');
  const { data, error } = await supabase.from('signed_record_signatures').insert({
    company_id: r.company_id, record_id: r.id, role: firmante.rol, worker_id: firmante.worker_id,
    signer_name: firmante.nombre.trim(), signer_cedula: firmante.cedula?.trim() || null, image_path: ruta, content_hash: hash,
  }).select('*');
  // La carpeta de firmas no la borra nadie (M45): si la fila falla, el trazo queda sin fila.
  if (error) falla(error, 'registrar la firma');
  if (!data?.length) throw new Error('No tienes permiso para registrar la firma.');
  return (data as FilaFirma[])[0];
}

// ── Adjuntos: escaneo firmado y certificado del médico ──

export async function revisarAdjunto(archivo: File): Promise<{ tipo: TipoEvidencia } | { error: string }> {
  const r = await revisarEvidencia(archivo);
  if ('error' in r) return r;
  if (r.tipo === 'image/webp') return { error: 'La foto debe ser JPG o PNG (o sube un PDF).' };
  if (r.tipo !== 'application/pdf' && archivo.size > FOTO_MAX_BYTES) return { error: `La foto supera ${FOTO_MAX_MB} MB.` };
  return r;
}

export async function subirAdjunto(r: FilaRegistro, archivo: File, para: 'escaneo' | 'certificado'): Promise<FilaRegistro> {
  if (r.status !== 'en_firma') throw new Error('Prepara el registro para firma antes de subir el documento firmado.');
  const rev = await revisarAdjunto(archivo);
  if ('error' in rev) throw new Error(rev.error);
  const esPdf = rev.tipo === 'application/pdf';
  const bucket: BucketRegistro = esPdf ? BUCKET_DOCUMENTOS : BUCKET_FIRMAS;
  const nombre = `${para}-${nombreSeguro(archivo.name, rev.tipo)}`;
  const ruta = esPdf ? rutaDocumento(r, nombre) : rutaRegistro(r, nombre);
  await subir(bucket, ruta, archivo.type === rev.tipo ? archivo : new File([archivo], nombre, { type: rev.tipo }), rev.tipo, 'subir el documento');
  const cambio = para === 'escaneo' ? { scan_bucket: bucket, scan_path: ruta } : { doctor_cert_bucket: bucket, doctor_cert_path: ruta };
  const { data, error } = await supabase.from('signed_records').update(cambio).eq('id', r.id).select('*');
  if (error) falla(error, 'guardar el documento en el registro');
  if (!data?.length) throw new Error('No tienes permiso para cambiar el registro.');
  return (data as FilaRegistro[])[0];
}

// ── Cerrar y reabrir ──

/** Marca el registro como firmado (la base revisa las firmas y pone la fecha y hora del servidor). */
export async function cerrarRegistro(r: FilaRegistro, metodo: MetodoRegistro): Promise<FilaRegistro> {
  const { data, error } = await supabase.from('signed_records').update({ status: 'firmado', sign_method: metodo }).eq('id', r.id).select('*');
  if (error) falla(error, 'cerrar el registro');
  if (!data?.length) throw new Error('No tienes permiso para cerrar el registro.');
  const reg = (data as FilaRegistro[])[0];
  registrarAuditoria(reg, 'registro_firmado', { metodo, hash: reg.content_hash });
  return reg;
}

export async function reabrirRegistro(id: string, motivo: string): Promise<void> {
  const { error } = await supabase.rpc('reabrir_registro_firmado', { p_registro: id, p_motivo: motivo });
  if (error) throw new Error(error.code === '42501' ? 'Solo el administrador de la empresa puede reabrir un registro firmado.' : mensajeErrorFirmas(error, 'reabrir el registro'));
}

/** Borra un registro SIN firmas (la base rechaza los que tienen firmas o están firmados; la RLS, a quien no es admin
 *  ni manager: 0 filas). Los archivos en Storage quedan (como las firmas: nadie borra en esos buckets). */
export async function borrarRegistro(r: FilaRegistro): Promise<void> {
  const { data, error } = await supabase.from('signed_records').delete().eq('id', r.id).select('id');
  if (error) falla(error, 'borrar el registro');
  if (!data?.length) throw new Error('No tienes permiso para borrar el registro.');
  logAudit({
    company_id: r.company_id, action: 'deleted', entity_type: 'signed_record', entity_id: r.id,
    entity_name: `${ETIQUETA_TIPO_REGISTRO[r.kind]} ${r.record_date.split('-').reverse().join('/')}`,
    details: { evento: 'registro_sin_firmas_borrado' },
  });
}

// ── Archivos ──

export async function urlArchivo(bucket: BucketRegistro, ruta: string, descarga?: string): Promise<string> {
  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(ruta, SEGUNDOS_URL_EVIDENCIA, descarga ? { download: descarga } : undefined);
  if (error || !data?.signedUrl) throw new Error('No se pudo abrir el archivo. Intenta de nuevo.');
  return data.signedUrl;
}

/** Trazo como data URL para el PDF (null si no se pudo leer). */
export async function dataUrlFirma(ruta: string): Promise<string | null> {
  const { data, error } = await supabase.storage.from(BUCKET_FIRMAS).download(ruta);
  if (error || !data) return null;
  return new Promise(resolve => {
    const lector = new FileReader();
    lector.onload = () => resolve(lector.result as string);
    lector.onerror = () => resolve(null);
    lector.readAsDataURL(data);
  });
}

function registrarAuditoria(r: FilaRegistro, evento: string, extra: Record<string, unknown>) {
  logAudit({
    company_id: r.company_id, action: 'updated', entity_type: 'signed_record', entity_id: r.id,
    entity_name: `${ETIQUETA_TIPO_REGISTRO[r.kind]} ${r.record_date.split('-').reverse().join('/')}`,
    details: { evento, ...extra },
  });
}
